import React, { useState, useEffect, useCallback, useMemo } from "react";
import { useNavigate } from "react-router-dom";
import CrmPage from "./CrmPage";
import { crmFetch, quoteStatus, formatMoney, formatDate, crmShareUrl } from "./crmApi";
import "./Crm.css";

const FILTERS = [
  { key: "all", label: "All" },
  { key: "sent", label: "📨 Awaiting reply" },
  { key: "accepted", label: "✅ Accepted" },
  { key: "declined", label: "🚫 Declined" },
];

/**
 * Every quote the studio has sent, in one list — the view the per-lead quote
 * card can't give you: what's outstanding, what's won, and what has gone quiet.
 */
function CrmQuotes() {
  const navigate = useNavigate();
  const [quotes, setQuotes] = useState([]);
  const [totals, setTotals] = useState(null);
  const [shareOrigin, setShareOrigin] = useState(null);
  const [loading, setLoading] = useState(true);
  const [planBlocked, setPlanBlocked] = useState(false);
  const [filter, setFilter] = useState("all");
  const [search, setSearch] = useState("");
  const [copiedId, setCopiedId] = useState(null);

  const load = useCallback(async () => {
    try {
      const d = await crmFetch("/api/crm/quotes");
      setQuotes(d.quotes || []);
      setTotals(d.totals || null);
      setShareOrigin(d.share_origin || null);
    } catch (e) {
      if (e.status === 401) navigate("/login");
      if (e.status === 403 && e.data?.code === "upgrade_required") setPlanBlocked(true);
    }
    setLoading(false);
  }, [navigate]);

  useEffect(() => { load(); }, [load]);

  const visible = useMemo(() => {
    let list = filter === "all" ? quotes : quotes.filter((q) => q.status === filter);
    const term = search.trim().toLowerCase();
    if (term) {
      list = list.filter((q) =>
        [q.client_name, q.quote_number, q.title, q.event_type]
          .some((v) => v && String(v).toLowerCase().includes(term))
      );
    }
    return list;
  }, [quotes, filter, search]);

  const copyLink = async (quote) => {
    const link = crmShareUrl(`/quote/${quote.share_token}`, shareOrigin);
    try {
      await navigator.clipboard.writeText(link);
      setCopiedId(quote.id);
      setTimeout(() => setCopiedId(null), 2000);
    } catch {
      window.prompt("Copy the quote link:", link);
    }
  };

  // A quote sitting unanswered past its validity date is the one worth chasing.
  const isStale = (q) =>
    q.status === "sent" && q.valid_until && new Date(q.valid_until) < new Date();

  return (
    <CrmPage
      title="📄 Quotes"
      subtitle="Every quote you've sent, and where it stands"
      loading={loading}
      planBlocked={planBlocked}
      actions={
        <button className="crm-primary-btn" onClick={() => navigate("/crm/leads")}>
          Go to leads →
        </button>
      }
    >
      {totals && totals.count > 0 && (
        <div className="crm-stats-row">
          <div className="crm-stat-card" style={{ borderTopColor: "#f59e0b" }}
               onClick={() => setFilter("sent")}>
            <div className="crm-stat-value">{totals.sent}</div>
            <div className="crm-stat-label">📨 Awaiting reply</div>
            {formatMoney(totals.pending_value) && (
              <div className="crm-stat-extra" style={{ color: "#f59e0b" }}>
                {formatMoney(totals.pending_value)} outstanding
              </div>
            )}
          </div>
          <div className="crm-stat-card" style={{ borderTopColor: "#10b981" }}
               onClick={() => setFilter("accepted")}>
            <div className="crm-stat-value">{totals.accepted}</div>
            <div className="crm-stat-label">✅ Accepted</div>
            {formatMoney(totals.won_value) && (
              <div className="crm-stat-extra">{formatMoney(totals.won_value)} won</div>
            )}
          </div>
          <div className="crm-stat-card" style={{ borderTopColor: "#dc2626" }}
               onClick={() => setFilter("declined")}>
            <div className="crm-stat-value">{totals.declined}</div>
            <div className="crm-stat-label">🚫 Declined</div>
          </div>
          <div className="crm-stat-card" style={{ borderTopColor: "#6b7280" }}
               onClick={() => setFilter("all")}>
            <div className="crm-stat-value">{totals.count}</div>
            <div className="crm-stat-label">📄 Total sent</div>
          </div>
        </div>
      )}

      <div className="crm-board-tools">
        <input
          className="crm-search"
          placeholder="🔍 Search by client, quote number or event…"
          value={search}
          onChange={(e) => setSearch(e.target.value)}
        />
        <div className="crm-stage-row" style={{ margin: 0 }}>
          {FILTERS.map((f) => (
            <button
              key={f.key}
              className={`crm-stage-chip ${filter === f.key ? "active" : ""}`}
              style={filter === f.key ? { background: "#a83c90" } : {}}
              onClick={() => setFilter(f.key)}
            >
              {f.label}
            </button>
          ))}
        </div>
      </div>

      {quotes.length === 0 ? (
        <div className="crm-empty">
          <span className="crm-empty-icon">📄</span>
          No quotes yet.
          <div className="crm-empty-hint">
            Open a lead and click "Create quote" — it'll show up here with its status.
          </div>
        </div>
      ) : visible.length === 0 ? (
        <div className="crm-empty">No quotes match that filter.</div>
      ) : (
        <div className="crm-table-wrap">
          <table className="crm-table">
            <thead>
              <tr>
                <th>Quote</th>
                <th>Client</th>
                <th>Total</th>
                <th>Status</th>
                <th>Sent</th>
                <th></th>
              </tr>
            </thead>
            <tbody>
              {visible.map((q) => {
                const st = quoteStatus(q.status);
                return (
                  <tr key={q.id} onClick={() => navigate(`/crm/leads?lead=${q.lead_id}`)}>
                    <td>
                      <div className="cell-main">{q.title || q.quote_number}</div>
                      <div className="cell-sub">
                        {q.quote_number}
                        {q.event_type ? ` · ${q.event_type}` : ""}
                      </div>
                    </td>
                    <td>
                      <div>{q.client_name}</div>
                      {q.client_phone && <div className="cell-sub">{q.client_phone}</div>}
                    </td>
                    <td className="cell-main" style={{ color: "#059669" }}>
                      {formatMoney(q.total) || "—"}
                    </td>
                    <td>
                      <span className="crm-badge" style={{ background: `${st.color}22`, color: st.color }}>
                        {st.icon} {st.label}
                      </span>
                      {isStale(q) && (
                        <div className="cell-sub" style={{ color: "#dc2626", fontWeight: 600 }}>
                          Expired {formatDate(q.valid_until)}
                        </div>
                      )}
                    </td>
                    <td>{formatDate(q.created_at)}</td>
                    <td onClick={(e) => e.stopPropagation()}>
                      <button className="crm-link-btn" onClick={() => copyLink(q)}>
                        {copiedId === q.id ? "✓ Copied" : "🔗 Link"}
                      </button>
                      <a
                        className="crm-link-btn"
                        href={crmShareUrl(`/quote/${q.share_token}`, shareOrigin)}
                        target="_blank"
                        rel="noopener noreferrer"
                      >
                        👁
                      </a>
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      )}
    </CrmPage>
  );
}

export default CrmQuotes;
